/**
 * Withdraw Page - Withdraw crypto assets from HybridX
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowUpFromLine,
  CheckCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  ShieldCheck,
  X,
  Plus,
  Trash2
} from 'lucide-react';
import { api } from '../utils/api';
import { cn } from '../utils/cn';
import { GasEstimateInline } from '../components/GasEstimate';

// Supported chains for withdrawals
const SUPPORTED_CHAINS = [
  { id: 1, name: 'Ethereum', symbol: 'ETH', icon: '⟠', fee: '0.005', minWithdraw: '0.01' },
  { id: 42161, name: 'Arbitrum', symbol: 'ETH', icon: '🔷', fee: '0.0001', minWithdraw: '0.001' },
  { id: 137, name: 'Polygon', symbol: 'MATIC', icon: '🟣', fee: '0.1', minWithdraw: '1' },
  { id: 56, name: 'BSC', symbol: 'BNB', icon: '🟡', fee: '0.0005', minWithdraw: '0.01' },
  { id: 8453, name: 'Base', symbol: 'ETH', icon: '🔵', fee: '0.0001', minWithdraw: '0.001' },
  { id: 10, name: 'Optimism', symbol: 'ETH', icon: '🔴', fee: '0.0001', minWithdraw: '0.001' },
];

// Token balances per chain (mock data structure)
interface TokenBalance {
  symbol: string;
  name: string;
  balance: string;
  available: string;
  usdValue: string;
  address: string;
  decimals: number;
}

// Withdrawal status badge
function StatusBadge({ status }: { status: string }) {
  const getStatusConfig = () => {
    switch (status) {
      case 'completed':
        return { 
          icon: CheckCircle, 
          text: 'Completed', 
          className: 'bg-green-500/20 text-green-400 border-green-500/30' 
        };
      case 'processing':
        return { 
          icon: RefreshCw, 
          text: 'Processing', 
          className: 'bg-blue-500/20 text-blue-400 border-blue-500/30 animate-pulse' 
        };
      case 'pending':
        return { 
          icon: Clock, 
          text: 'Pending Approval', 
          className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' 
        };
      case 'approved':
        return { 
          icon: ShieldCheck, 
          text: 'Approved', 
          className: 'bg-primary/20 text-primary border-primary/30' 
        };
      case 'failed':
        return { 
          icon: AlertTriangle, 
          text: 'Failed', 
          className: 'bg-red-500/20 text-red-400 border-red-500/30' 
        };
      case 'rejected':
        return { 
          icon: X, 
          text: 'Rejected', 
          className: 'bg-red-500/20 text-red-400 border-red-500/30' 
        };
      default:
        return { 
          icon: Clock, 
          text: status, 
          className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' 
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.className}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.text}
    </span>
  );
}

// Address validation
function isValidAddress(address: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

export default function Withdraw() {
  const queryClient = useQueryClient();
  const [selectedChain, setSelectedChain] = useState(SUPPORTED_CHAINS[0]);
  const [selectedToken, setSelectedToken] = useState<TokenBalance | null>(null);
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showWhitelistModal, setShowWhitelistModal] = useState(false);
  const [newWhitelistAddress, setNewWhitelistAddress] = useState('');
  const [newWhitelistLabel, setNewWhitelistLabel] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [confirmStep, setConfirmStep] = useState<'details' | '2fa'>('details');

  // Check if 2FA is enabled
  const { data: twoFactorStatus } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/2fa/status', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        return data.data;
      } catch {
        return { enabled: false };
      }
    },
  });

  // Fetch balances
  const { data: balances, isLoading: balancesLoading } = useQuery({
    queryKey: ['custodial-balances', selectedChain.id],
    queryFn: async () => {
      try {
        const response = await api.get(`/hybrid/balance?chainId=${selectedChain.id}`);
        return response.data.balances;
      } catch (error) {
        // Mock data for development
        return [
          { symbol: 'ETH', name: 'Ethereum', balance: '1.5432', available: '1.5432', usdValue: '5234.56', address: 'NATIVE', decimals: 18 },
          { symbol: 'USDT', name: 'Tether USD', balance: '10000.00', available: '9500.00', usdValue: '10000.00', address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6 },
          { symbol: 'USDC', name: 'USD Coin', balance: '5000.00', available: '5000.00', usdValue: '5000.00', address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6 },
        ] as TokenBalance[];
      }
    },
  });

  // Fetch whitelist
  const { data: whitelist, isLoading: whitelistLoading } = useQuery({
    queryKey: ['withdrawal-whitelist', selectedChain.id],
    queryFn: async () => {
      try {
        const response = await api.get(`/withdrawal-whitelist?chainId=${selectedChain.id}`);
        return response.data;
      } catch (error) {
        // Mock data
        return [
          { id: '1', address: '0x742d35Cc6634C0532925a3b844Bc9e7595f8fE21', label: 'My MetaMask' },
          { id: '2', address: '0xAb5801a7D398351b8bE11C439e05C5B3259aeC9B', label: 'Hardware Wallet' },
        ];
      }
    },
  });

  // Fetch withdrawal history
  const { data: withdrawals, isLoading: withdrawalsLoading } = useQuery({
    queryKey: ['withdrawals'],
    queryFn: async () => {
      try {
        const response = await api.get('/withdrawals');
        return response.data;
      } catch (error) {
        // Mock data
        return [
          {
            id: '1',
            asset: 'ETH',
            amount: '0.5',
            chainId: 1,
            address: '0x742d...fE21',
            status: 'completed',
            txHash: '0x1234...abcd',
            fee: '0.005',
            createdAt: new Date(Date.now() - 86400000).toISOString(),
          },
          {
            id: '2',
            asset: 'USDT',
            amount: '500',
            chainId: 1,
            address: '0xAb58...eC9B',
            status: 'processing',
            txHash: null,
            fee: '5',
            createdAt: new Date(Date.now() - 3600000).toISOString(),
          },
        ];
      }
    },
    refetchInterval: 10000,
  });

  // Withdraw mutation
  const withdrawMutation = useMutation({
    mutationFn: async (data: { chainId: number; token: string; amount: string; address: string; twoFactorCode?: string }) => {
      const response = await api.post('/hybrid/withdraw', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
      queryClient.invalidateQueries({ queryKey: ['custodial-balances'] });
      setShowConfirmModal(false);
      setAmount('');
      setAddress('');
      setTwoFactorCode('');
      setConfirmStep('details');
    },
    onError: () => {
      setTwoFactorCode('');
    },
  });

  // Add to whitelist mutation
  const addWhitelistMutation = useMutation({
    mutationFn: async (data: { chainId: number; address: string; label: string }) => {
      const response = await api.post('/withdrawal-whitelist', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdrawal-whitelist'] });
      setShowWhitelistModal(false);
      setNewWhitelistAddress('');
      setNewWhitelistLabel('');
    },
  });

  // Set default token when balances load
  useEffect(() => {
    if (balances && balances.length > 0 && !selectedToken) {
      setSelectedToken(balances[0]);
    }
  }, [balances, selectedToken]);

  // Calculate receive amount after fees
  const calculateReceiveAmount = () => {
    if (!amount || !selectedToken) return '0';
    const amountNum = parseFloat(amount);
    const fee = selectedToken.symbol === selectedChain.symbol 
      ? parseFloat(selectedChain.fee) 
      : 5; // $5 for ERC20 tokens (approx)
    const receive = amountNum - fee;
    return receive > 0 ? receive.toFixed(6) : '0';
  };

  // Format time ago
  const timeAgo = (date: string) => {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Handle max button
  const handleMax = () => {
    if (selectedToken) {
      setAmount(selectedToken.available);
    }
  };

  // Validate withdrawal
  const canWithdraw = () => {
    if (!amount || !address || !selectedToken) return false;
    if (!isValidAddress(address)) return false;
    const amountNum = parseFloat(amount);
    const availableNum = parseFloat(selectedToken.available);
    const minNum = parseFloat(selectedChain.minWithdraw);
    if (amountNum <= 0 || amountNum > availableNum) return false;
    if (amountNum < minNum) return false;
    return true;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Withdraw</h1>
          <p className="text-gray-400 mt-1">Withdraw crypto assets from your HybridX account</p>
        </div>
        <button
          onClick={() => setShowWhitelistModal(true)}
          className="btn btn-secondary flex items-center gap-2"
        >
          <ShieldCheck className="w-5 h-5" />
          Manage Whitelist
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Withdrawal Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Chain Selection */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Select Network</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {SUPPORTED_CHAINS.map((chain) => (
                <button
                  key={chain.id}
                  onClick={() => {
                    setSelectedChain(chain);
                    setSelectedToken(null);
                  }}
                  className={`p-4 rounded-xl border transition-all ${
                    selectedChain.id === chain.id
                      ? 'border-primary bg-primary/10'
                      : 'border-dark-100 hover:border-dark-50 bg-dark-200/50'
                  }`}
                >
                  <div className="text-2xl mb-2">{chain.icon}</div>
                  <div className="font-medium text-sm">{chain.name}</div>
                  <div className="text-xs text-gray-500">Fee: {chain.fee}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Token & Amount */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Select Token & Amount</h2>
            
            {/* Token Selection */}
            <div className="mb-6">
              <label className="block text-sm text-gray-400 mb-2">Token</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {balancesLoading ? (
                  <div className="col-span-3 flex justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : balances && balances.length > 0 ? (
                  balances.map((token: TokenBalance) => (
                    <button
                      key={token.symbol}
                      onClick={() => setSelectedToken(token)}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        selectedToken?.symbol === token.symbol
                          ? 'border-primary bg-primary/10'
                          : 'border-dark-100 hover:border-dark-50 bg-dark-200/50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-bold">{token.symbol}</span>
                        <span className="text-xs text-gray-500">${token.usdValue}</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        Available: {parseFloat(token.available).toLocaleString()}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="col-span-3 text-center py-8 text-gray-400">
                    No balances available
                  </div>
                )}
              </div>
            </div>

            {/* Amount Input */}
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <label className="text-sm text-gray-400">Amount</label>
                {selectedToken && (
                  <span className="text-sm text-gray-400">
                    Available: <span className="text-white">{selectedToken.available}</span> {selectedToken.symbol}
                  </span>
                )}
              </div>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="input w-full pr-20"
                />
                <button
                  onClick={handleMax}
                  className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1 bg-primary/20 text-primary text-xs font-medium rounded hover:bg-primary/30 transition-colors"
                >
                  MAX
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Minimum withdrawal: {selectedChain.minWithdraw} {selectedChain.symbol}
              </p>
            </div>

            {/* Destination Address */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Destination Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="0x..."
                className={`input w-full font-mono ${
                  address && !isValidAddress(address) ? 'border-red-500' : ''
                }`}
              />
              {address && !isValidAddress(address) && (
                <p className="text-xs text-red-400 mt-2">Invalid address format</p>
              )}
              
              {/* Quick Select from Whitelist */}
              {whitelist && whitelist.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Or select from whitelist:</p>
                  <div className="flex flex-wrap gap-2">
                    {whitelist.map((item: any) => (
                      <button
                        key={item.id}
                        onClick={() => setAddress(item.address)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                          address === item.address
                            ? 'bg-primary/20 text-primary border border-primary/30'
                            : 'bg-dark-200 hover:bg-dark-100 text-gray-400 border border-transparent'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Summary & Submit */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Withdrawal Summary</h2>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-400">Amount</span>
                <span>{amount || '0'} {selectedToken?.symbol}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Network Fee</span>
                <span className="text-yellow-400">
                  -{selectedChain.fee} {selectedChain.symbol}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Est. Gas Cost</span>
                <GasEstimateInline chainId={selectedChain.id} txType="erc20" />
              </div>
              <hr className="border-dark-100" />
              <div className="flex justify-between text-lg font-semibold">
                <span>You Receive</span>
                <span className="text-green-400">
                  {calculateReceiveAmount()} {selectedToken?.symbol}
                </span>
              </div>
            </div>

            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={!canWithdraw()}
              className="btn btn-primary w-full py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Withdraw
            </button>
          </div>

          {/* Withdrawal History */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Withdrawal History</h2>
            
            {withdrawalsLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : withdrawals && withdrawals.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-400 text-sm border-b border-dark-100">
                      <th className="pb-3 font-medium">Asset</th>
                      <th className="pb-3 font-medium">Amount</th>
                      <th className="pb-3 font-medium">Address</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-100">
                    {withdrawals.map((withdrawal: any) => (
                      <tr key={withdrawal.id} className="text-sm">
                        <td className="py-4 font-medium">{withdrawal.asset}</td>
                        <td className="py-4">{withdrawal.amount}</td>
                        <td className="py-4 font-mono text-gray-400 text-xs">
                          {withdrawal.address}
                        </td>
                        <td className="py-4">
                          <StatusBadge status={withdrawal.status} />
                        </td>
                        <td className="py-4 text-gray-400">
                          {timeAgo(withdrawal.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <ArrowUpFromLine className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No withdrawals yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Withdrawal Limits */}
          <div className="card p-6">
            <h3 className="font-semibold mb-4">Withdrawal Limits</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Daily Limit</span>
                  <span>$50,000</span>
                </div>
                <div className="w-full bg-dark-200 rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full" style={{ width: '15%' }} />
                </div>
                <p className="text-xs text-gray-500 mt-1">$7,500 / $50,000 used</p>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Monthly Limit</span>
                  <span>$500,000</span>
                </div>
                <div className="w-full bg-dark-200 rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full" style={{ width: '8%' }} />
                </div>
                <p className="text-xs text-gray-500 mt-1">$40,000 / $500,000 used</p>
              </div>
            </div>
          </div>

          {/* Security Tips */}
          <div className="card p-6">
            <h3 className="font-semibold mb-4">Security Tips</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-2">
                <ShieldCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-400">Always double-check the destination address</span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-400">Ensure you're sending to the correct network</span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-400">Use whitelisted addresses for extra security</span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-400">Enable 2FA for withdrawal approvals</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="card max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {confirmStep === 'details' ? 'Confirm Withdrawal' : 'Enter 2FA Code'}
              </h3>
              <button 
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmStep('details');
                  setTwoFactorCode('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {confirmStep === 'details' ? (
              <>
                <div className="space-y-4 mb-6">
                  <div className="bg-dark-200 rounded-lg p-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-400">Sending</span>
                      <span className="font-bold text-lg">{amount} {selectedToken?.symbol}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Network</span>
                      <span>{selectedChain.icon} {selectedChain.name}</span>
                    </div>
                  </div>

                  <div className="bg-dark-200 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">To Address</p>
                    <p className="font-mono text-sm break-all">{address}</p>
                  </div>

                  <div className="bg-dark-200 rounded-lg p-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-400">Network Fee</span>
                      <span>-{selectedChain.fee} {selectedChain.symbol}</span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span>You Receive</span>
                      <span className="text-green-400">{calculateReceiveAmount()} {selectedToken?.symbol}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <p className="text-sm text-gray-300">
                      Please verify the address and network are correct. Transactions cannot be reversed.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      setShowConfirmModal(false);
                      setConfirmStep('details');
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (twoFactorStatus?.enabled) {
                        setConfirmStep('2fa');
                      } else {
                        withdrawMutation.mutate({
                          chainId: selectedChain.id,
                          token: selectedToken?.address || 'NATIVE',
                          amount,
                          address,
                        });
                      }
                    }}
                    disabled={withdrawMutation.isPending}
                    className="btn btn-primary"
                  >
                    {withdrawMutation.isPending ? (
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                    ) : twoFactorStatus?.enabled ? (
                      'Next: Verify 2FA'
                    ) : (
                      'Confirm Withdrawal'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                    <div className="flex gap-3">
                      <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
                      <p className="text-sm text-gray-300">
                        Enter the 6-digit code from your authenticator app to confirm this withdrawal.
                      </p>
                    </div>
                  </div>

                  <div className="bg-dark-200 rounded-lg p-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-400">Amount</span>
                      <span className="font-medium">{amount} {selectedToken?.symbol}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">To</span>
                      <span className="font-mono text-sm">{address.slice(0, 8)}...{address.slice(-6)}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">2FA Code</label>
                    <input
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="input w-full text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Or enter a backup code
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      setConfirmStep('details');
                      setTwoFactorCode('');
                    }}
                    className="btn btn-secondary"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => withdrawMutation.mutate({
                      chainId: selectedChain.id,
                      token: selectedToken?.address || 'NATIVE',
                      amount,
                      address,
                      twoFactorCode,
                    })}
                    disabled={twoFactorCode.length < 6 || withdrawMutation.isPending}
                    className="btn btn-primary"
                  >
                    {withdrawMutation.isPending ? (
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                    ) : (
                      'Confirm Withdrawal'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Whitelist Modal */}
      {showWhitelistModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="card max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Withdrawal Whitelist</h3>
              <button 
                onClick={() => setShowWhitelistModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Add New Address */}
            <div className="bg-dark-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium mb-3">Add New Address</h4>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newWhitelistAddress}
                  onChange={(e) => setNewWhitelistAddress(e.target.value)}
                  placeholder="0x..."
                  className="input w-full font-mono text-sm"
                />
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newWhitelistLabel}
                    onChange={(e) => setNewWhitelistLabel(e.target.value)}
                    placeholder="Label (e.g., My MetaMask)"
                    className="input flex-1"
                  />
                  <button
                    onClick={() => addWhitelistMutation.mutate({
                      chainId: selectedChain.id,
                      address: newWhitelistAddress,
                      label: newWhitelistLabel,
                    })}
                    disabled={!isValidAddress(newWhitelistAddress) || !newWhitelistLabel}
                    className="btn btn-primary px-4"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Existing Addresses */}
            <div>
              <h4 className="font-medium mb-3">Whitelisted Addresses</h4>
              {whitelist && whitelist.length > 0 ? (
                <div className="space-y-2">
                  {whitelist.map((item: any) => (
                    <div 
                      key={item.id}
                      className="flex items-center justify-between bg-dark-200 rounded-lg p-3"
                    >
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="font-mono text-xs text-gray-400">{item.address}</p>
                      </div>
                      <button className="text-gray-400 hover:text-red-400 transition-colors">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">No whitelisted addresses</p>
              )}
            </div>

            <button
              onClick={() => setShowWhitelistModal(false)}
              className="btn btn-secondary w-full mt-6"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
